/**
 * Network Monitor Service
 * Tracks network connectivity state across all platforms (web, iOS, Android)
 * Uses Navigator.onLine API which works universally
 */

export type NetworkState = 'online' | 'offline' | 'unknown';
export type NetworkHandler = (isOnline: boolean, state: NetworkState) => void;

interface NetworkInfo {
  isOnline: boolean;
  state: NetworkState;
  lastOnlineAt: Date | null;
  lastOfflineAt: Date | null;
  offlineDuration: number | null; // ms since going offline
}

class NetworkMonitorService {
  private handlers: Set<NetworkHandler> = new Set();
  private currentState: NetworkState = 'unknown';
  private lastOnlineAt: Date | null = null;
  private lastOfflineAt: Date | null = null;
  private initialized = false;

  /**
   * Initialize the network monitor
   * Safe to call multiple times - will only initialize once
   */
  initialize(): void {
    if (this.initialized) {
      return;
    }

    // Set initial state
    this.currentState = navigator.onLine ? 'online' : 'offline';
    if (this.currentState === 'online') {
      this.lastOnlineAt = new Date();
    } else {
      this.lastOfflineAt = new Date();
    }

    // Listen for online event
    window.addEventListener('online', () => {
      this.currentState = 'online';
      this.lastOnlineAt = new Date();
      this.notifyHandlers(true);
    });

    // Listen for offline event
    window.addEventListener('offline', () => {
      this.currentState = 'offline';
      this.lastOfflineAt = new Date();
      this.notifyHandlers(false);
    });

    this.initialized = true;
  }

  /**
   * Notify all subscribed handlers of state change
   */
  private notifyHandlers(isOnline: boolean): void {
    this.handlers.forEach(handler => {
      try {
        handler(isOnline, this.currentState);
      } catch (error) {
        console.error('[NetworkMonitor] Handler error:', error);
      }
    });
  }

  /**
   * Subscribe to network state changes
   * @returns Unsubscribe function
   */
  subscribe(handler: NetworkHandler): () => void {
    this.handlers.add(handler);

    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Get current online status
   */
  get isOnline(): boolean {
    // Always check navigator.onLine for most accurate state
    return navigator.onLine;
  }

  /**
   * Get current network state
   */
  getState(): NetworkState {
    return this.currentState;
  }

  /**
   * Get detailed network info
   */
  getNetworkInfo(): NetworkInfo {
    const isOnline = this.isOnline;
    return {
      isOnline,
      state: this.currentState,
      lastOnlineAt: this.lastOnlineAt,
      lastOfflineAt: this.lastOfflineAt,
      offlineDuration: !isOnline && this.lastOfflineAt
        ? Date.now() - this.lastOfflineAt.getTime()
        : null
    };
  }

  /**
   * Check if we've been offline for longer than specified duration
   * @param durationMs Duration in milliseconds
   */
  hasBeenOfflineFor(durationMs: number): boolean {
    if (this.isOnline) return false;
    if (!this.lastOfflineAt) return false;

    return Date.now() - this.lastOfflineAt.getTime() > durationMs;
  }

  /**
   * Wait for network to come back online
   * @param timeoutMs Maximum time to wait (default: 30 seconds)
   * @returns Promise that resolves when online, or rejects on timeout
   */
  waitForOnline(timeoutMs: number = 30000): Promise<void> {
    return new Promise((resolve, reject) => {
      // Already online
      if (this.isOnline) {
        resolve();
        return;
      }

      let unsubscribe: (() => void) | null = null;
      let timeoutId: NodeJS.Timeout | null = null;

      const cleanup = () => {
        if (unsubscribe) unsubscribe();
        if (timeoutId) clearTimeout(timeoutId);
      };

      // Subscribe to online event
      unsubscribe = this.subscribe((isOnline) => {
        if (isOnline) {
          cleanup();
          resolve();
        }
      });

      // Set timeout
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Network timeout: still offline'));
      }, timeoutMs);
    });
  }

  /**
   * Cleanup (call when app is unmounting)
   */
  cleanup(): void {
    this.handlers.clear();
    this.initialized = false;
  }
}

// Export singleton instance
export const networkMonitorService = new NetworkMonitorService();
